/**
 * Authentication utilities
 *
 * Handles token storage, validation, and auth state checking.
 */

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

interface JwtPayload {
  exp?: number;
}

export interface StoredUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  roles: string[];
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set access token
 */
export function setAccessToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Set refresh token
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

/**
 * Get stored user data
 */
export function getStoredUser(): StoredUser | null {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

/**
 * Set user data
 */
export function setStoredUser(user: StoredUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Check if user has a specific role
 */
export function hasRole(user: StoredUser | null, role: string): boolean {
  if (!user) return false;
  const targetRole = role.toLowerCase();
  return user.roles.some((userRole) => userRole.toLowerCase() === targetRole);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(user: StoredUser | null, roles: string[]): boolean {
  if (!user) return false;
  return roles.some((role) => hasRole(user, role));
}

/**
 * Clear all auth data
 */
export function clearAuthData(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function decodeJwtPayload(token: string): JwtPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  const payloadBase64 = parts[1]
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');

  try {
    const decoded = typeof atob === 'function'
      ? atob(payloadBase64)
      : Buffer.from(payloadBase64, 'base64').toString('utf8');
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowInSeconds;
}

/**
 * Get token expiry time in milliseconds (Unix timestamp)
 * Returns null if token is invalid or has no expiry
 */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp * 1000; // Convert to milliseconds
}

/**
 * Get time remaining until token expires (in milliseconds)
 * Returns 0 if token is expired or invalid
 */
export function getTokenTimeRemaining(token: string): number {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;

  const now = Date.now();
  return Math.max(0, expiry - now);
}

/**
 * Check if user appears authenticated (has token and user data)
 */
export function isAuthenticated(): boolean {
  const token = getAccessToken();
  const user = getStoredUser();
  if (!token || !user) {
    return false;
  }

  if (isTokenExpired(token)) {
    clearAuthData();
    return false;
  }

  return true;
}

/**
 * Refresh token using the API
 * @returns true if refresh succeeded, false otherwise
 */
export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    if (data.success && data.data?.accessToken) {
      setAccessToken(data.data.accessToken);
      if (data.data.refreshToken) {
        setRefreshToken(data.data.refreshToken);
      }
      return true;
    }
  } catch {
    // Refresh failed
  }

  return false;
}

/**
 * Refresh threshold in milliseconds (5 minutes)
 * Tokens will be refreshed proactively when they have less than this time remaining
 */
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Check if token should be refreshed proactively
 * (less than 5 minutes remaining)
 */
export function shouldRefreshToken(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  const timeRemaining = getTokenTimeRemaining(token);
  return timeRemaining > 0 && timeRemaining < REFRESH_THRESHOLD_MS;
}
